import { ChatComposeBoxAddAttachmentMenuItem } from '@glade-ai/chat-plugin/ui';
import React, { type ComponentPropsWithoutRef, type FC, forwardRef, useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import SearchInput from '@/components/FormFields/SearchInput';
import FileText from '@/components/Icons/FileText';
import ProgressIndicator from '@/components/ProgressIndicator';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import * as tsClient from '@/tsClient';
import type * as ApiModels from '@/typings/api-models';

import s from '../InputComposer.module.scss';

type Props = {
  disabled: boolean;
  onAttach: (data: { title: string; formTemplateId: string }) => void;
};

const ADD_FORM_REQUEST_LABEL = 'Form Request';
const ADD_FORM_REQUEST_ICON = 'ph--file-text--regular';

export const AddFormRequestMenuItem = forwardRef<
  HTMLDivElement,
  Omit<ComponentPropsWithoutRef<typeof ChatComposeBoxAddAttachmentMenuItem>, 'icon' | 'label'>
>((props, forwardedRef) => (
  <ChatComposeBoxAddAttachmentMenuItem
    {...props}
    ref={forwardedRef}
    icon={ADD_FORM_REQUEST_ICON}
    label={ADD_FORM_REQUEST_LABEL}
  />
));
AddFormRequestMenuItem.displayName = 'AddFormRequestMenuItem';

export const AddFormRequestPopoverContent: FC<Omit<Props, 'disabled'>> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [search, setSearch] = useState('');
  const {
    data: formTemplates,
    getData: getFormTemplates,
    fetchingState: { isFetching: isFetchingFormTemplates },
  } = useNoodleApi(tsClient.formRequests.getCreatorFormTemplates);

  useEffect(() => {
    if (creatorId) {
      getFormTemplates({
        creatorId,
        page: 1,
        perPage: 1000,
        requireValidQuestionnaire: true,
      });
    }
  }, [creatorId, getFormTemplates]);

  const handleClick = (template: Pick<ApiModels.FormRequestTemplate, 'id' | 'name'>): void => {
    onAttach({ formTemplateId: template.id, title: template.name });
  };

  if (formTemplates?.items.length === 0) {
    return null;
  }

  return (
    <>
      {!formTemplates || isFetchingFormTemplates ? (
        <ProgressIndicator isCentered />
      ) : (
        <>
          <SearchInput
            label='Filter by title'
            placeholder='Type a title...'
            setSearch={setSearch}
            className={s.search}
          />
          {formTemplates.items
            .filter((template) => template.name.toLowerCase().includes(search.toLowerCase()))
            .map((template) => (
              <Buttons
                className={s.documentRequestButton}
                key={template.id}
                isWrapper
                isFlat
                onClick={() => handleClick(template)}
              >
                <FileText size={14} weight='fill' />
                <p>{template.name}</p>
              </Buttons>
            ))}
        </>
      )}
    </>
  );
};
